import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useI18n } from "@/lib/i18n";
import { MessageSquare, X, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const uiText = {
  fr: {
    open: "Assistant",
    title: "Assistant Entretien Pro",
    subtitle: "Réponses en direct — nettoyage commercial & industriel.",
    placeholder: "Posez votre question…",
    intro: "Bonjour 👋 Je peux répondre à vos questions sur nos services, notre méthodologie ou notre zone de service. Comment puis-je vous aider?",
    error: "Erreur de connexion. Réessayez.",
  },
  en: {
    open: "Assistant",
    title: "Entretien Pro Assistant",
    subtitle: "Live answers — commercial & industrial cleaning.",
    placeholder: "Ask your question…",
    intro: "Hi 👋 I can answer questions about our services, methodology or service area. How can I help?",
    error: "Connection error. Please retry.",
  },
};

export default function Chatbot() {
  const { locale } = useI18n();
  const ui = uiText[locale];
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem("chat_session") || null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setLoading(true);

    // Append placeholder for assistant streaming
    setMessages((m) => [...m, { role: "assistant", content: "" }]);

    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: text, locale }),
      });
      if (!res.ok || !res.body) throw new Error("bad response");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const part of parts) {
          if (!part.startsWith("data:")) continue;
          const jsonStr = part.replace(/^data:\s*/, "").trim();
          if (!jsonStr) continue;
          try {
            const evt = JSON.parse(jsonStr);
            if (evt.type === "session" && evt.session_id) {
              setSessionId(evt.session_id);
              localStorage.setItem("chat_session", evt.session_id);
            } else if (evt.type === "delta") {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = {
                  ...copy[copy.length - 1],
                  content: copy[copy.length - 1].content + evt.content,
                };
                return copy;
              });
            } else if (evt.type === "error") {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: ui.error };
                return copy;
              });
            }
          } catch (_) {}
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: ui.error };
        return copy;
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Floating open button */}
      <motion.button
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1.8, duration: 0.5 }}
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-5 right-5 md:bottom-6 md:right-6 z-[60] bg-[#E1FF00] hover:bg-[#CBE600] text-[#050505] px-4 py-3 flex items-center gap-2 font-mono-acc text-[11px] tracking-[0.15em] uppercase font-semibold shadow-[0_10px_40px_-10px_rgba(225,255,0,0.6)]"
        data-testid="chatbot-toggle"
        aria-label={ui.open}
      >
        {open ? <X size={16} /> : <MessageSquare size={16} />}
        <span>{ui.open}</span>
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            key="chat-panel"
            initial={{ opacity: 0, y: 30, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ duration: 0.25, ease: [0.76, 0, 0.24, 1] }}
            className="fixed bottom-20 right-5 md:bottom-24 md:right-6 z-[60] w-[calc(100vw-2.5rem)] sm:w-[400px] h-[560px] max-h-[80vh] bg-[#0a0a0a] border border-[#27272A] flex flex-col shadow-2xl"
            data-testid="chatbot-panel"
          >
            {/* Header */}
            <div className="px-5 py-4 border-b border-[#27272A] bg-[#050505]">
              <div className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-[#E1FF00] rounded-full animate-pulse" />
                <div className="font-mono-acc text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA]">
                  LIVE · GPT-5.4 mini
                </div>
              </div>
              <div className="mt-1 font-display text-lg text-[#F4F4F5] tracking-tight">
                {ui.title}
              </div>
              <div className="text-xs text-[#A1A1AA] mt-0.5">{ui.subtitle}</div>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chatbot-messages">
              <div className="flex">
                <div className="max-w-[85%] bg-[#121212] border border-[#27272A] px-4 py-3 text-sm text-[#F4F4F5] leading-relaxed">
                  {ui.intro}
                </div>
              </div>
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : ""}`}>
                  <div
                    className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
                      m.role === "user"
                        ? "bg-[#E1FF00] text-[#050505]"
                        : "bg-[#121212] border border-[#27272A] text-[#F4F4F5]"
                    }`}
                    data-testid={`chatbot-msg-${m.role}`}
                  >
                    {m.content || (loading && m.role === "assistant" ? "…" : "")}
                  </div>
                </div>
              ))}
            </div>

            {/* Input */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
              className="border-t border-[#27272A] p-3 flex items-center gap-2 bg-[#050505]"
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={ui.placeholder}
                className="flex-1 bg-transparent border border-[#27272A] focus:border-[#E1FF00] px-3 py-2.5 text-[#F4F4F5] placeholder:text-[#52525B] text-sm transition-colors"
                data-testid="chatbot-input"
                disabled={loading}
              />
              <button
                type="submit"
                disabled={loading || !input.trim()}
                className="bg-[#E1FF00] hover:bg-[#CBE600] disabled:opacity-40 text-[#050505] p-2.5 transition-colors"
                data-testid="chatbot-send"
                aria-label="send"
              >
                <Send size={16} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
